/**
 * 261018 대표 주차판 시연(10/16 17:14) — 앱이 '맡긴 QR'을 되찾아 주차판 등록 화면을 연 지 2초 만에
 * 이미 지운 업체 상세(빈 화면에 '영업시간')로 튕겼다.
 *
 * 원인은 안드로이드 MainActivity — 푸시를 눌러 켠 앱은 그 인텐트(callbackName·callbackParams)를 지우지 않는다.
 * 앱이 백그라운드에서 꺼졌다가(메모리 회수) 다시 만들어지면 같은 인텐트로 onCreate 가 돌고, checkMoveLink 가
 * 그 옛 푸시 이동(fnRouterPush)을 다시 쏜다. 대표님 폰은 그날 '신규 가게 입점'(업체 186) 푸시로 켠 앱이었다.
 * 네이티브 수정은 앱 업데이트가 있어야 나간다. 그 전에 웹에서 가를 수 있는 건 이 경우다:
 * 방금 맡긴 QR 로 화면을 열었다면 그 사람은 QR 때문에 앱을 연 것이다 — 그 직후 몇 초 안에 오는 푸시 이동은 되살아난 옛 알림이다.
 * (그 몇 초 사이에 새 알림을 눌러 들어오는 일은 사실상 없다.)
 */
const GUARD_MS = 10000;
let resumedAt = 0;
// 261023 이어가기 이동이 아직 끝나지 않았다(App.vue 가 router.push 를 기다리는 중). 부팅 마무리(goHomeUnlessDeepLinked)는
//   이 동안만 홈으로 덮지 않는다 — 처음엔 10초(isJustAfterQrResume)로 막았는데, 그 사이 로그인 화면에서 뒤로 가면
//   '/'(빈 첫 화면)에 남았다(운영 화면 재현: 3초에 뒤로 → 흰 화면). 이동이 멈춰 버려도 30초 뒤엔 놓는다.
const NAVIGATING_MAX_MS = 30000;
let navigating = false;

/** App.vue#resumePendingQrClaim 이 QR 화면으로 보내기 직전에 부른다. */
export const markQrResumeNavigated = () => {
  resumedAt = Date.now();
  navigating = true;
};

/** App.vue#resumePendingQrClaim — 그 이동이 끝나면(성공·로그인으로 돌림·실패 모두) 부른다. */
export const markQrResumeSettled = () => {
  navigating = false;
};

/** NativeCall.js#fnRouterPush — 참이면 그 푸시 이동을 버린다. */
export const isJustAfterQrResume = () => resumedAt > 0 && Date.now() - resumedAt < GUARD_MS;

/** NativeCall.js#goHomeUnlessDeepLinked — 참이면 홈으로 덮지 않는다. */
export const isQrResumeNavigating = () => navigating && Date.now() - resumedAt < NAVIGATING_MAX_MS;

/**
 * 261023 유저 "재발방지" — 이어가기로 연 주차판을 등록이 끝날 때까지 폰에 기억한다(하루).
 *   아이폰 15:18 기록: 맡긴 코드로 로그인 화면까지 열었다가 13초 뒤 뒤로('/')·홈으로 빠졌다. 맡긴 코드는 서버에서 이미
 *   소비돼(pending-claim 1회) 판을 다시 찍기 전엔 길이 없었다 → 홈에 닿으면 App.vue#offerParkingResume 가 한 번 묻는다.
 *   지우는 때 = 등록 성공(QrParkingRegister) · '나중에' · 로그아웃(store resetStoreOnLogout) · 하루 지남.
 */
export const PARKING_RESUME_KEY = 'jump.qr.parkingResume';
const PARKING_RESUME_MS = 24 * 60 * 60 * 1000;

export const rememberParkingResume = qrCode => {
  try {
    localStorage.setItem(PARKING_RESUME_KEY, JSON.stringify({ qrCode, at: Date.now() }));
  } catch (_) {
    /* 저장소 차단 — 안전망 하나를 놓칠 뿐 */
  }
};

export const pendingParkingResume = () => {
  try {
    const v = JSON.parse(localStorage.getItem(PARKING_RESUME_KEY) || 'null');
    if (v && v.qrCode && Date.now() - v.at < PARKING_RESUME_MS) return v;
    localStorage.removeItem(PARKING_RESUME_KEY);
  } catch (_) {
    /* 깨진 값·저장소 차단 */
  }
  return null;
};

export const clearParkingResume = qrCode => {
  const v = pendingParkingResume();
  if (!v || (qrCode && String(v.qrCode).toUpperCase() !== String(qrCode).toUpperCase())) return;
  try {
    localStorage.removeItem(PARKING_RESUME_KEY);
  } catch (_) {
    /* 무시 */
  }
};

/**
 * 261023 운영 화면 E2E — 사파리에서 맡긴 뒤(앱 없음 → /p/ → 스토어) 앱을 깔고 다시 눌러 앱 링크로 열면, 앱 링크(App.vue watch)와
 *   맡긴 코드(App.vue#resumePendingQrClaim)가 같은 판으로 같이 온다. 앞의 이동이 로그인 화면을 연 뒤 뒤의 이동이 같은 곳을 또 밀면
 *   '로그인 후 이용가능합니다' 창(URL_GUARD)이 로그인 화면 위에 남았다 — 로그인 화면에서 로그인 화면으로 다시 돌리는 이동은 생략돼
 *   창을 닫는 길(updateLayout)을 안 탄다. 이미 그 화면이거나, 로그인 화면이 그곳을 복귀 경로로 들고 있으면 또 밀지 않는다.
 */
export const isAlreadyHeadingTo = (target, route, loginMovePath) =>
  route?.fullPath === target || (route?.path === '/signin' && loginMovePath === target);
